<template lang="pug">
study-detail.study-management-detail(
  ref='template',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :breadcrumbMoveEvent='breadcrumbMoveEvent',
  :breadcrumbButtonEvent='breadcrumbButtonEvent',
  :category='category',
  :sort='sort',
  :studyDetails='studyDetails',
  :numberSelectOptions='numberSelectOptions',
  :endConditionSelectOptions='endConditionSelectOptions',
  :tabHeadingEvent='tabHeadingEvent',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#breadcrumbButtonLabel) 保存する
  template(#subjectHeader) 教科
  template(#selectionHeader) 選択
  template(#numberHeader) 受験回数
  template(#endConditionHeader) 終了条件
  template(#periodStartDateHeader) 受験可能期間（開始）
  template(#periodEndDateHeader) 受験可能期間（終了）
  template(#registrationDateHeader) 登録日
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'
import StudyDetail from '@/components/templates/manager/StudyDetail.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudyDetailType, StudyDetailSortType, StudyDetailSearchParamsType } from '@/models/studyDetailModel'
import { InputItem } from '@/models/inputItem'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { SortSettings } from '@/const/SortSettings'
import { SUnitCategorySetting } from '@/const/ApiSettings'
import { ManagerInputUtils } from '@/utils/ManagerInputUtils'
import { StudyManagementUtils } from '@/utils/StudyManagementUtils'

/**
 * システム管理者・テスト詳細画面
 */
@Component({
  components: {
    StudyDetail,
  },
})
export default class StudyManagementDetail extends Mixins(LoginMethods, OriginalCurriculumsApi) {
  // テンプレートへのアクセス
  @Ref() template!: StudyDetail

  // テストID
  private get studyId(): number {
    return Number(this.$route.params.studyId)
  }

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'テスト管理',
      location: { name: 'AdminStudyManagement' },
    },
    {
      label: 'テスト詳細設定_',
    },
  ]

  // システム管理者権限
  private userRole: UserRoles = UserRoles.ADMIN

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_STUDY

  // ソート情報
  private sort: StudyDetailSortType | null = null

  // テスト情報詳細一覧
  private studyDetails: StudyDetailType[] = []

  // 受験回数選択オプション
  private numberSelectOptions: InputItem[] = []

  // 終了条件選択オプション
  private endConditionSelectOptions: InputItem[] = []

  // 小項目区分
  private category: SUnitCategorySetting = SUnitCategorySetting.LAST_MINUTE_CHECK

  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして検索する
    this.setDefaultSort()
    await this.search()
    // タイトル文字列を取得
    const apiResponse = await this.getOriginalCurriculumInfo(this.studyId)
    this.breadcrumbs[1].label += StudyManagementUtils.buildStudyContentTitle(apiResponse)
    // 受験回数選択オプション、終了条件選択オプションを設定
    this.numberSelectOptions = ManagerInputUtils.buildNumberInputItems()
    this.endConditionSelectOptions = ManagerInputUtils.buildEndConditionInputItems()
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      subject: SortSettings.ASC,
      selection: SortSettings.ASC,
      number: SortSettings.ASC,
      endCondition: SortSettings.ASC,
      periodStartDate: SortSettings.ASC,
      periodEndDate: SortSettings.ASC,
    }
  }

  private async sortChanged(sort: StudyDetailSortType) {
    this.$loading.start()
    // ソートを更新して検索する
    this.sort = sort
    await this.search()
    this.$loading.complete()
  }

  // 検索処理
  private async search() {
    if (!this.sort) {
      return
    }
    // テスト詳細一覧情報API呼び出し
    const params: StudyDetailSearchParamsType = {
      category: this.category,
      subjectSort: this.sort.subject.code,
      nameSort: this.sort.selection.code,
      numberSort: this.sort.number.code,
      endConditionSort: this.sort.endCondition.code,
      periodStartDateSort: this.sort.periodStartDate.code,
      periodEndDateSort: this.sort.periodEndDate.code,
    }
    const apiResponse = await this.getOriginalCurriculumUnitList(this.studyId, params, this.userRole)
    this.studyDetails = StudyManagementUtils.formatStudyDetailList(apiResponse)
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Admin${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AdminLogin' })
  }

  // パンくず遷移イベント
  private breadcrumbMoveEvent(breadcrumb: breadcrumbType) {
    if (breadcrumb.location) {
      this.$router.push(breadcrumb.location)
    }
  }

  // パンくずパネル内ボタンイベント
  private async breadcrumbButtonEvent() {
    // 詳細設定一覧を保存する
    this.$loading.start()
    const settings = StudyManagementUtils.buildOriginalCurriculumUnitSettings(this.studyDetails)
    await this.updateOriginalCurriculumUnitList(settings, this.userRole)
    // 再検索する
    await this.search()
    this.$loading.complete()
  }

  // 見出しタブ押下イベント
  private async tabHeadingEvent(category: SUnitCategorySetting) {
    this.$loading.start()
    // 区分を変更
    this.category = category
    // 一旦一覧情報を初期化
    this.studyDetails = []
    // ソート値をデフォルトに戻して再検索
    this.setDefaultSort()
    await this.search()
    this.$loading.complete()
  }
}
</script>
